import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Zap, Loader2 } from 'lucide-react';
import { loginUser, registerUser, requestPasswordReset } from '../lib/api';

// Czytelne etykiety branż — read-back pod selektorem neutralizuje „ciche" zmiany
// z typeahead klawiatury Radix Select (user widział skok LED→Meble). Audyt 2026-07-05.
const INDUSTRY_LABELS = {
  oswietlenie: 'Oświetlenie / LED', hvac: 'HVAC / Klimatyzacja', meble: 'Meble / Wyposażenie',
  elektryka: 'Elektryka / Instalacje', gaz: 'Gaz / Instalacje gazowe', chemia: 'Chemia',
  rolnictwo: 'Produkty rolne', fintech: 'Fintech / IT', energetyka: 'Energetyka / OZE',
  nieruchomosci: 'Nieruchomości / Deweloperka', inne: 'Inne',
};

export default function AuthPage({ onAuth }) {
  const [loading, setLoading] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [registerForm, setRegisterForm] = useState({
    email: '', password: '', confirmPassword: '', name: '', company: '', language: 'pl', industry: 'oswietlenie'
  });

  async function handleLogin(e) {
    e.preventDefault();
    if (!loginForm.email || !loginForm.password) {
      toast.error('Email i has\u0142o s\u0105 wymagane');
      return;
    }
    setLoading(true);
    try {
      const result = await loginUser(loginForm);
      localStorage.setItem('token', result.access_token);
      localStorage.setItem('user', JSON.stringify(result.user));
      onAuth(result);
      toast.success('Zalogowano pomy\u015blnie');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'B\u0142\u0105d logowania');
    }
    setLoading(false);
  }

  async function handleForgotPassword() {
    // Backend zwraca generyczn\u0105 odpowied\u017a (nie zdradza czy email istnieje) \u2014 komunikat te\u017c generyczny.
    if (!loginForm.email || !loginForm.email.includes('@')) {
      toast.error('Wpisz email w polu powy\u017cej, aby zresetowa\u0107 has\u0142o');
      return;
    }
    try {
      await requestPasswordReset(loginForm.email);
      toast.success('Je\u015bli konto istnieje, wys\u0142ali\u015bmy instrukcj\u0119 resetu na e-mail.');
    } catch (err) {
      toast.error('Nie uda\u0142o si\u0119 wys\u0142a\u0107 pro\u015bby o reset has\u0142a');
    }
  }

  async function handleRegister(e) {
    e.preventDefault();
    if (!registerForm.email || !registerForm.password || !registerForm.name) {
      toast.error('Imi\u0119, email i has\u0142o s\u0105 wymagane');
      return;
    }
    if (registerForm.password.length < 8) {
      toast.error('Has\u0142o musi mie\u0107 min. 8 znak\u00f3w');
      return;
    }
    if (registerForm.password !== registerForm.confirmPassword) {
      toast.error('Has\u0142a nie s\u0105 identyczne');
      return;
    }
    setLoading(true);
    try {
      const result = await registerUser(registerForm);
      localStorage.setItem('token', result.access_token);
      localStorage.setItem('user', JSON.stringify(result.user));
      onAuth(result);
      toast.success('Konto utworzone!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'B\u0142\u0105d rejestracji');
    }
    setLoading(false);
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="auth-page">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-2xl bg-primary flex items-center justify-center mx-auto mb-4">
            <Zap className="w-7 h-7 text-primary-foreground" />
          </div>
          <h1 className="text-3xl font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk' }}>
            MASKIL
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {"Autonomiczny agent przetargowo-leadowy B2B"}
          </p>
        </div>

        <Card className="border border-border">
          <CardContent className="p-6">
            <Tabs defaultValue="login">
              <TabsList className="w-full bg-muted/50 mb-6">
                <TabsTrigger value="login" className="flex-1" data-testid="login-tab">
                  Logowanie
                </TabsTrigger>
                <TabsTrigger value="register" className="flex-1" data-testid="register-tab">
                  Rejestracja
                </TabsTrigger>
              </TabsList>

              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div>
                    <Label className="text-xs">Email</Label>
                    <Input
                      type="email"
                      value={loginForm.email}
                      onChange={e => setLoginForm(p => ({...p, email: e.target.value}))}
                      placeholder="jan@firma.pl"
                      className="mt-1"
                      data-testid="login-email"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">{"Hasło"}</Label>
                    <Input
                      type="password"
                      value={loginForm.password}
                      onChange={e => setLoginForm(p => ({...p, password: e.target.value}))}
                      placeholder="********"
                      className="mt-1"
                      data-testid="login-password"
                    />
                  </div>
                  <button type="button" onClick={handleForgotPassword}
                          className="text-xs text-muted-foreground hover:text-foreground underline"
                          data-testid="forgot-password">
                    {"Zapomniałeś hasła?"}
                  </button>
                  <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    {"Zaloguj się"}
                  </Button>
                </form>
              </TabsContent>

              <TabsContent value="register">
                <form onSubmit={handleRegister} className="space-y-4">
                  <div>
                    <Label className="text-xs">{"Imię i nazwisko"} *</Label>
                    <Input
                      value={registerForm.name}
                      onChange={e => setRegisterForm(p => ({...p, name: e.target.value}))}
                      placeholder="Jan Kowalski"
                      className="mt-1"
                      data-testid="register-name"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Firma</Label>
                    <Input
                      value={registerForm.company}
                      onChange={e => setRegisterForm(p => ({...p, company: e.target.value}))}
                      placeholder="Nazwa firmy"
                      className="mt-1"
                      data-testid="register-company"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Email *</Label>
                    <Input
                      type="email"
                      value={registerForm.email}
                      onChange={e => setRegisterForm(p => ({...p, email: e.target.value}))}
                      placeholder="jan@firma.pl"
                      className="mt-1"
                      data-testid="register-email"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">{"Hasło (min. 8 znaków, 2 typy znaków)"} *</Label>
                    <Input
                      type="password"
                      value={registerForm.password}
                      onChange={e => setRegisterForm(p => ({...p, password: e.target.value}))}
                      placeholder="********"
                      className="mt-1"
                      data-testid="register-password"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">{"Powtórz hasło"} *</Label>
                    <Input
                      type="password"
                      value={registerForm.confirmPassword}
                      onChange={e => setRegisterForm(p => ({...p, confirmPassword: e.target.value}))}
                      placeholder="********"
                      className="mt-1"
                      data-testid="register-confirm-password"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">{"Język"}</Label>
                    <Select value={registerForm.language} onValueChange={v => setRegisterForm(p => ({...p, language: v}))}>
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pl">Polski</SelectItem>
                        <SelectItem value="en">English</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">{"Branża (kierunek konta — stała po założeniu)"}</Label>
                    <Select value={registerForm.industry} onValueChange={v => setRegisterForm(p => ({...p, industry: v}))}>
                      <SelectTrigger className="mt-1" data-testid="register-industry">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="oswietlenie">Oświetlenie / LED</SelectItem>
                        <SelectItem value="hvac">HVAC / Klimatyzacja</SelectItem>
                        <SelectItem value="meble">Meble / Wyposażenie</SelectItem>
                        <SelectItem value="elektryka">Elektryka / Instalacje</SelectItem>
                        <SelectItem value="gaz">Gaz / Instalacje gazowe</SelectItem>
                        <SelectItem value="chemia">Chemia</SelectItem>
                        <SelectItem value="rolnictwo">Produkty rolne</SelectItem>
                        <SelectItem value="fintech">Fintech / IT</SelectItem>
                        <SelectItem value="energetyka">Energetyka / OZE</SelectItem>
                        <SelectItem value="nieruchomosci">Nieruchomości / Deweloperka</SelectItem>
                        <SelectItem value="inne">Inne</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-[11px] text-muted-foreground mt-1">
                      {"Wybrana branża: "}<span className="font-medium">{INDUSTRY_LABELS[registerForm.industry] || registerForm.industry}</span>{" — stała po założeniu konta."}
                    </p>
                  </div>
                  <Button type="submit" className="w-full" disabled={loading} data-testid="register-submit">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    {"Utwórz konto"}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        <p className="text-xs text-muted-foreground text-center mt-4">
          MASKIL v3 {"•"} {"Adam — autonomiczny agent przetargowy"}
        </p>
      </div>
    </div>
  );
}
